import React from 'react';
import {
  ApartmentOutlined,
  AuditOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileProtectOutlined,
  MailOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  ShareAltOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { getContactEmail } from '../../../../core/config/runtimeConfig';
import LegalPage, { LegalNavigationItem, LegalSummaryItem } from './LegalPage';
import LegalSection from './LegalSection';

const UPDATED_AT = 'August 12, 2026';

const navigation: readonly LegalNavigationItem[] = [
  { id: 'overview', label: 'Overview', icon: <AuditOutlined /> },
  { id: 'roles', label: 'Privacy roles', icon: <ApartmentOutlined /> },
  {
    id: 'information',
    label: 'Information we handle',
    icon: <DatabaseOutlined />,
  },
  { id: 'use', label: 'How we use it', icon: <FileProtectOutlined /> },
  { id: 'ai', label: 'AI-assisted features', icon: <RobotOutlined /> },
  { id: 'sharing', label: 'Sharing', icon: <ShareAltOutlined /> },
  { id: 'retention', label: 'Retention', icon: <ClockCircleOutlined /> },
  {
    id: 'rights',
    label: 'Privacy rights',
    icon: <SafetyCertificateOutlined />,
  },
  { id: 'contact', label: 'Contact', icon: <MailOutlined /> },
] as const;

const summaryItems: readonly LegalSummaryItem[] = [
  {
    title: 'Built for authorized teams',
    description:
      'Access is limited to recruiters and administrators who need the information for hiring work.',
    icon: <TeamOutlined />,
  },
  {
    title: 'Hiring data has a purpose',
    description:
      'Candidate and company information supports recruiting, communication, scheduling, and reporting.',
    icon: <FileProtectOutlined />,
  },
  {
    title: 'Human review remains essential',
    description:
      'AI summaries and matching evidence assist recruiters; they do not make employment decisions.',
    icon: <RobotOutlined />,
  },
] as const;

type PrivacyPolicyProps = {
  embedded?: boolean;
};

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ embedded = false }) => {
  const contactEmail = getContactEmail();

  return (
    <LegalPage
      embedded={embedded}
      title="Privacy Policy"
      description="How the ATS handles recruiter, company, and candidate information."
      updatedAt={UPDATED_AT}
      icon={<SafetyCertificateOutlined />}
      navigation={navigation}
      summaryTitle="At a glance"
      summaryItems={summaryItems}
    >
      <LegalSection id="overview" title="Overview">
        <p>
          This Privacy Policy explains how Jobs &amp; Opportunities handles
          personal information through its applicant tracking system (“ATS”). It
          applies to account holders, recruiters, administrators, candidates,
          and people who use secure public links for interviews, clarifications,
          or candidate conversations.
        </p>
        <p>
          The ATS supports candidate records, job postings, applications, hiring
          stages, communications, interviews, files, and assisted recruiting
          tools. Additional notices may appear when information is collected for
          a specific workflow.
        </p>
      </LegalSection>

      <LegalSection id="roles" title="Our role and your organization's role">
        <div className="ats-legal-callout ats-legal-callout--info">
          <ApartmentOutlined aria-hidden="true" />
          <div>
            <h3>Responsibility depends on the relationship</h3>
            <p>
              The organization using the ATS generally decides why and how its
              candidate and employee information is used. In that context, the
              organization is typically the controller or responsible party, and
              Jobs &amp; Opportunities processes information to provide the
              service. We may act as a controller for account, security,
              billing, service analytics, and direct support information.
            </p>
          </div>
        </div>
        <p>
          Candidates should contact the recruiting organization first for
          questions about a particular application or hiring process. We will
          assist organizations with valid privacy requests where required.
        </p>
      </LegalSection>

      <LegalSection id="information" title="Information we handle">
        <div className="ats-legal-table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Examples</th>
                <th scope="col">Primary source</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Account and security</td>
                <td>
                  Name, work email, phone, profile photo, role, login and 2FA
                  status
                </td>
                <td>Account holders and identity systems</td>
              </tr>
              <tr>
                <td>Organization and job data</td>
                <td>
                  Company and client contacts, jobs, templates, stages,
                  assignments
                </td>
                <td>Recruiters and administrators</td>
              </tr>
              <tr>
                <td>Candidate and application data</td>
                <td>
                  Contact details, resume, skills, salary, profile URLs, notes,
                  status history
                </td>
                <td>Candidates, recruiters, referrals, and uploaded files</td>
              </tr>
              <tr>
                <td>Communications and scheduling</td>
                <td>
                  Messages, clarification responses, interview proposals,
                  calendar details
                </td>
                <td>Recruiters, candidates, and connected services</td>
              </tr>
              <tr>
                <td>Technical and usage</td>
                <td>
                  IP address, browser, device, logs, feature activity, and
                  preferences
                </td>
                <td>Devices, application logs, and analytics</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Recruiters should collect only information that is relevant, lawful,
          and necessary for the hiring process. Sensitive information should not
          be added unless there is a valid legal and business need.
        </p>
      </LegalSection>

      <LegalSection id="use" title="How we use information">
        <ul>
          <li>
            Authenticate users and enforce organization and role-based access.
          </li>
          <li>
            Manage jobs, candidates, applications, clients, stages, and
            assignments.
          </li>
          <li>
            Store candidate files and maintain recruiting activity history.
          </li>
          <li>
            Support messages, clarification requests, interviews, and
            notifications.
          </li>
          <li>
            Generate requested recruiting summaries, evidence, and career
            materials.
          </li>
          <li>Secure, troubleshoot, maintain, and improve the ATS.</li>
          <li>
            Provide support, prevent abuse, and comply with legal obligations.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="ai" title="AI-assisted recruiting features">
        <div className="ats-legal-callout ats-legal-callout--info">
          <RobotOutlined aria-hidden="true" />
          <div>
            <h3>Decision support, not automated hiring</h3>
            <p>
              The ATS may summarize candidate information, identify matching
              evidence, suggest skills, draft content, or highlight gaps. These
              outputs may be incomplete or inaccurate and must be reviewed by an
              authorized person. The ATS does not make final employment
              decisions on behalf of an organization.
            </p>
          </div>
        </div>
        <p>
          Information submitted to an assisted feature may be processed by
          service providers as needed to return the requested result. Access to
          generated summaries and evidence follows the same organization and
          permission controls as the underlying candidate record.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="When information is shared">
        <p>Information may be disclosed to:</p>
        <ul>
          <li>
            <strong>Authorized users in the recruiting organization</strong>,
            according to their roles and assigned hiring work.
          </li>
          <li>
            <strong>Candidates and interview participants</strong>, when a user
            sends a message, clarification, interview proposal, or secure link.
          </li>
          <li>
            <strong>Service providers</strong> supporting hosting, storage,
            authentication, email, communications, analytics, security, and
            assisted features.
          </li>
          <li>
            <strong>Authorities or transaction parties</strong>, when required
            by law or reasonably necessary to protect the service, users, or
            rights, or in connection with a business transfer.
          </li>
        </ul>
        <p>We do not sell candidate or account-holder personal information.</p>
      </LegalSection>

      <LegalSection id="security" title="Security and access controls">
        <p>
          We use safeguards designed to protect information, including
          authentication, organization-scoped access, role controls, secure
          links, and operational monitoring. Account holders are responsible for
          protecting credentials, enabling available security features, and
          promptly reporting suspected unauthorized access.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="Retention and deletion">
        <p>
          Information is retained for as long as reasonably needed to provide
          the ATS, support active recruiting records, meet contractual and legal
          requirements, resolve disputes, and maintain security. Retention may
          vary by organization, record type, account status, and applicable law.
        </p>
        <p>
          An organization may define its own hiring-record retention rules.
          Deleted information may remain in protected backups for a limited
          period before being removed through normal backup rotation.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="Privacy rights and choices">
        <p>
          Depending on applicable law, individuals may request access,
          correction, deletion, restriction, portability, or objection to
          certain processing. Account holders can update some profile and
          notification preferences directly in the ATS.
        </p>
        <p>
          Candidate requests about an application should normally be sent to the
          recruiting organization. We may verify identity and route the request
          to the appropriate organization before taking action.
        </p>
      </LegalSection>

      <LegalSection id="international" title="International processing">
        <p>
          We and our providers may process information in countries other than
          the country where it was collected. Where required, appropriate
          contractual or legal safeguards are used for international transfers.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to this policy">
        <p>
          We may update this policy as the ATS, our providers, or legal
          requirements change. The date above identifies the latest version.
          Material changes will receive additional notice where required.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact us">
        <p>
          Account holders may contact their organization administrator or the
          support channel supplied with their account. Privacy questions for
          Jobs &amp; Opportunities may be sent
          {contactEmail ? (
            <>
              {' '}
              to <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
            </>
          ) : (
            <>
              {' '}
              through the platform support contact provided by your
              organization.
            </>
          )}
        </p>
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPolicy;
